import React, { useEffect, useState, useCallback, useRef } from 'react';
import { initializeItinerarySocket, processItinerarySteps, findCurrentWaypoint, sendStartBreak, sendRequestSplitShift } from './itinerarySocketHelper';
import ItineraryItinerary from './ItineraryItinerary';
import FlagTripCreateModal from './FlagTripCreateModal';
import ItineraryMap from './ItineraryMap';
import ItineraryCurrentWaypoint from './ItineraryCurrentWaypoint';
import GeolocationHandler from '../LiveDispatch/GeolocationHandler';
import comms from '../../comms';

function ItineraryDispatch({
  socket: passedSocket,
  fleetSettings,
  userSettings,
  driverData,
  navigateTo,
  routeDetails: initialRouteDetails,
  selectedVehicle,
  onBack,
  isDispatchMode = false
}) {
  const [googleApiKey, setGoogleApiKey] = useState(null);
  const [map, setMap] = useState(null);
  const [isAccurate, setIsAccurate] = useState(false);
  // Use routeDetails startLat/startLon as initial center if available, otherwise use default
  const getInitialCenter = () => {
    if (initialRouteDetails?.startLat && initialRouteDetails?.startLon) {
      return { lat: initialRouteDetails.startLat, lng: initialRouteDetails.startLon };
    }
    // Use fleet config GPS if available, otherwise fall back to generic US center
    if (fleetSettings?.centerLat && fleetSettings?.centerLon) {
      return { lat: fleetSettings.centerLat, lng: fleetSettings.centerLon };
    }
    return { lat: 39.8283, lng: -98.5795 };
  };
  const [center, setCenter] = useState(getInitialCenter);
  const [loading, setLoading] = useState(true);
  const [isPhone, setIsPhone] = useState(false);
  const [vehiclePosition, setVehiclePosition] = useState(null);
  
  // Route state - managed by socket updates
  const [routeDetails, setRouteDetails] = useState(initialRouteDetails);
  const [currentWaypoint, setCurrentWaypoint] = useState(null);
  const [schedule, setSchedule] = useState([]);
  
  // FEB 2026: Sync routeDetails state when initialRouteDetails prop changes (e.g., re-authentication)
  // This fixes the bug where stale route data from yesterday is used after driver re-logs in
  useEffect(() => {
    if (initialRouteDetails?.routeId && initialRouteDetails.routeId !== routeDetails?.routeId) {
      console.log('[ItineraryDispatch] Route changed from props, updating state:', 
        routeDetails?.routeId, '->', initialRouteDetails.routeId);
      setRouteDetails(initialRouteDetails);
      // Clear any stale persisted data to prevent future mismatches
      comms.clearPersistedRouteData();
    }
  }, [initialRouteDetails?.routeId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Active break state - tracks if driver is currently on break (even if break is not currentWaypoint)
  const [activeBreak, setActiveBreak] = useState(null);

  // Phase B — itinerary-mode flag trip modal
  const [showFlagTripModal, setShowFlagTripModal] = useState(false);
  
  // Itinerary change tracking for toast notifications
  const [itineraryChangeToast, setItineraryChangeToast] = useState(null);
  const itinerarySnapshotRef = useRef(new Map()); // Map of StepId -> { tripNo, jobNo, stepType }
  const lastActionSourceRef = useRef(null); // Track if last action was from this client
  
  // Connection status state for toast display
  const [connectionError, setConnectionError] = useState(null);
  
  // Refs for GPS timing and map reference
  const lastLocationRef = useRef(null);
  const lastServerUpdateRef = useRef(0);
  const mapContainerRef = useRef(null);
  
  // Track whether we're on a phone vs tablet/desktop
  // Respect fleet setting 'displayOrientation' to force portrait or landscape layout
  useEffect(() => {
    const handleResize = () => {
      // Check if fleet setting 'displayOrientation' is set
      if (fleetSettings?.displayOrientation === 'portrait') {
        setIsPhone(true); // Force portrait layout
      } else if (fleetSettings?.displayOrientation === 'landscape') {
        setIsPhone(false); // Force landscape layout
      } else {
        // Default responsive behavior based on window width
        setIsPhone(window.innerWidth < 768);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fleetSettings?.displayOrientation]);
  
  // Load Google Maps API key from fleetSettings (like FixedShuttlebus does)
  useEffect(() => {
    if (fleetSettings && fleetSettings.googleApiKey) {
      setGoogleApiKey(fleetSettings.googleApiKey);
      setLoading(false);
    }
  }, [fleetSettings]);
  
  // Initialize itinerary snapshot from initial route details
  // This allows us to detect changes in subsequent routeUpdate messages
  useEffect(() => {
    if (initialRouteDetails?.itinerary && itinerarySnapshotRef.current.size === 0) {
      const initialSnapshot = new Map();
      initialRouteDetails.itinerary.forEach(step => {
        const key = step.StepId || step.stepId;
        if (key) {
          initialSnapshot.set(key, {
            tripNo: step.tripNo || step.TripNo,
            jobNo: step.JobNo || step.jobNo,
            stepType: step.type || step.StepType
          });
        }
      });
      itinerarySnapshotRef.current = initialSnapshot;
      console.log('Initialized itinerary snapshot with', initialSnapshot.size, 'steps');
    }
  }, [initialRouteDetails]);
  
  // Subscribe to connection status changes for reconnection handling
  useEffect(() => {
    let wasDisconnected = false;
    
    const unsubscribe = comms.onConnectionStatusChange((status, previousStatus) => {
      console.log('ItineraryDispatch connection status:', status, 'previous:', previousStatus);
      
      // Track disconnection
      if (status === 'disconnected' || status === 'error') {
        wasDisconnected = true;
        setConnectionError(null); // Clear any previous error
      }
      
      // Show error toast on failed status
      if (status === 'failed') {
        setConnectionError('Connection failed. Tap the status icon to reconnect.');
      }
      
      // Clear error on successful connection
      if (status === 'connected') {
        setConnectionError(null);
        
        // Reload route data after reconnection
        if (wasDisconnected && routeDetails?.routeId) {
          console.log('Reconnected - reloading itinerary data...');
          comms.getRouteDetails(routeDetails.routeId)
            .then((updatedRouteData) => {
              console.log('Route data reloaded after reconnection:', updatedRouteData);
              if (updatedRouteData) {
                setRouteDetails(updatedRouteData);
              }
            })
            .catch((err) => {
              console.error('Failed to reload route data after reconnection:', err);
            });
        }
        wasDisconnected = false;
      }
    });
    
    return unsubscribe;
  }, [routeDetails?.routeId]);
  
  // Initialize socket listeners for itinerary updates
  useEffect(() => {
    if (!passedSocket) {
      console.warn('Socket not provided to ItineraryDispatch - real-time updates disabled');
      return;
    }
    
    console.log('Initializing itinerary socket listeners...');
    
    // Set active route for centralized room management
    if (initialRouteDetails?.routeId) {
      comms.setActiveRoute(initialRouteDetails.routeId);
    }
    
    const cleanup = initializeItinerarySocket(passedSocket, (updatedRouteData, metadata = {}) => {
      console.log('ItineraryDispatch received route update:', updatedRouteData, 'metadata:', metadata);
      
      // Check if this update came from cross-room broadcaster (dispatch console)
      const isExternalUpdate = metadata.isExternal === true;
      
      console.log('Route update - source:', metadata.source, 'isExternal:', isExternalUpdate);
      
      // Track itinerary changes for toast notification - ONLY for external updates from cross-room broadcaster
      if (isExternalUpdate && updatedRouteData?.itinerary && itinerarySnapshotRef.current.size > 0) {
        const newSnapshot = new Map();
        const changes = { added: [], removed: [], updated: false };
        
        // Build new snapshot and detect additions
        updatedRouteData.itinerary.forEach(step => {
          const key = step.StepId || step.stepId;
          const stepInfo = {
            tripNo: step.tripNo || step.TripNo,
            jobNo: step.JobNo || step.jobNo,
            stepType: step.type || step.StepType
          };
          newSnapshot.set(key, stepInfo);
          
          if (!itinerarySnapshotRef.current.has(key)) {
            changes.added.push(stepInfo);
          }
        });
        
        // Detect removals
        itinerarySnapshotRef.current.forEach((stepInfo, key) => {
          if (!newSnapshot.has(key)) {
            changes.removed.push(stepInfo);
          }
        });
        
        // Update snapshot
        itinerarySnapshotRef.current = newSnapshot;
        
        // Show toast if there are changes (added/removed trips)
        const totalChanges = changes.added.length + changes.removed.length;
        if (totalChanges > 0) {
          setItineraryChangeToast(prev => ({
            added: (prev?.added || 0) + changes.added.length,
            removed: (prev?.removed || 0) + changes.removed.length
          }));
        } else {
          // No trips added/removed, but still an external update - show generic update toast
          // This covers time changes, reordering, etc.
          console.log('External route update received with no trip changes - showing update toast');
          setItineraryChangeToast({ updated: true, added: 0, removed: 0 });
        }
      } else if (updatedRouteData?.itinerary) {
        // First load or self-initiated update - just build snapshot
        const newSnapshot = new Map();
        updatedRouteData.itinerary.forEach(step => {
          const key = step.StepId || step.stepId;
          newSnapshot.set(key, {
            tripNo: step.tripNo || step.TripNo,
            jobNo: step.JobNo || step.jobNo,
            stepType: step.type || step.StepType
          });
        });
        itinerarySnapshotRef.current = newSnapshot;
      }
      
      // Check for active break in updated data
      // Note: Check multiple fields since raw itinerary uses StepDescription/StepType/breakStep, 
      // while processed steps use type
      const breakStep = updatedRouteData?.itinerary?.find(step => {
        const stepType = (step.type || step.StepType || step.StepDescription || '').toLowerCase();
        const isBreakStep = stepType === 'break' || step.breakStep === true;
        const isArrived = step.StepStatus === 'arrived' || step.isArrived === true;
        return isBreakStep && isArrived && !step.isCompleted;
      });
      // Enrich breakStep with vehicleId and routeId if found
      if (breakStep) {
        // Use fallback chain for vehicleId
        const vehicleId = selectedVehicle?.Id 
          || selectedVehicle?.id 
          || driverData?.dispatchData?.vehicleId
          || driverData?.dispatchData?.vehicleObject?.id
          || driverData?.dispatchData?.vehicleObject?.Id;
        setActiveBreak({
          ...breakStep,
          vehicleId: vehicleId,
          routeId: updatedRouteData.routeId
        });
      } else {
        setActiveBreak(null);
      }
      
      // Check for split shift approval notification
      const approvedBreak = updatedRouteData?.itinerary?.find(step => {
        const stepType = (step.type || '').toLowerCase();
        return stepType === 'break' && step.StepStatus === 'splitShiftApproved';
      });
      if (approvedBreak) {
        // Show approval toast
        setConnectionError('Split shift request approved! You can now clock out.');
        setTimeout(() => setConnectionError(null), 8000);
      }
      
      // Check for split shift rejection notification
      const rejectedBreak = updatedRouteData?.itinerary?.find(step => {
        const stepType = (step.type || '').toLowerCase();
        return stepType === 'break' && step.StepStatus === 'splitShiftRejected';
      });
      if (rejectedBreak) {
        // Show rejection toast
        setConnectionError('Split shift request was declined');
        setTimeout(() => setConnectionError(null), 5000);
      }
      
      setRouteDetails(updatedRouteData);
    });
    
    return cleanup;
  }, [passedSocket, initialRouteDetails?.routeId]);
  
  // Helper function to get vehicleId with fallback chain
  // This ensures we always have a vehicleId even if selectedVehicle prop is empty
  const getVehicleId = useCallback(() => {
    return selectedVehicle?.Id 
      || selectedVehicle?.id 
      || driverData?.dispatchData?.vehicleId
      || driverData?.dispatchData?.vehicleObject?.id
      || driverData?.dispatchData?.vehicleObject?.Id
      || null;
  }, [selectedVehicle, driverData]);

  // ── Phase B flag-trip gate (client-side; server independently re-checks) ─────
  // Show the Flag button only when: fleet flag on, no passenger on board, not on
  // break, and the next scheduled pickup is ≥ 15 min away (or none).
  const lc = (s) => String(s || '').toLowerCase();
  const hasLoadedTrip = !!currentWaypoint &&
    (lc(currentWaypoint.type) === 'delivery' || lc(currentWaypoint.type) === 'dropoff');
  const onBreak = !!activeBreak;
  const nextPickupStep = (schedule || []).find(
    (s) => lc(s.type || s.StepDescription) === 'pickup' && !s.isCompleted
  );
  const nextPickupRaw = nextPickupStep
    ? (nextPickupStep.StartDateTime || nextPickupStep.eta || nextPickupStep.trip?.pickup?.date)
    : null;
  const minutesUntilNextPickup = nextPickupRaw
    ? Math.round((new Date(nextPickupRaw).getTime() - Date.now()) / 60000)
    : Infinity;
  // TEMP (testing): force-enable the Phase B flag-trip button for the ACET fleet,
  // regardless of the fleetSettings.flagTrip value, so it shows on ACET. Other
  // fleets still honour fleetSettings.flagTrip. NOTE: the server independently
  // enforces its own flagTrip on rates/quote/create.
  const fc = String(fleetSettings?.unitId || localStorage.getItem('fleetCode') || '').toUpperCase();
  const fu = String(fleetSettings?.fleetUrl || '').toLowerCase();
  const isAcetFleet = fc.includes('ACET') || fu.includes('acet');
  const flagTripEnabled = fleetSettings?.flagTrip === true || isAcetFleet;
  const canFlag =
    flagTripEnabled &&
    !hasLoadedTrip &&
    !onBreak &&
    minutesUntilNextPickup >= 15;
  const openFlagTrip = useCallback(() => setShowFlagTripModal(true), []);

  // Process itinerary and find current waypoint when routeDetails changes
  useEffect(() => {
    if (!routeDetails || !routeDetails.itinerary || !routeDetails.trips) {
      setSchedule([]);
      setCurrentWaypoint(null);
      return;
    }
    
    console.log('[ItineraryDispatch] Processing routeDetails update, itinerary steps:', routeDetails.itinerary?.length);
    console.log('[ItineraryDispatch] First 3 itinerary steps isCompleted:', 
      routeDetails.itinerary?.slice(0, 3).map(s => ({ 
        stepType: s.StepDescription, 
        isCompleted: s.isCompleted,
        _optimistic: s._optimistic 
      }))
    );
    
    // Update active route if routeId changed
    if (routeDetails.routeId) {
      comms.setActiveRoute(routeDetails.routeId);
    }
    
    // Get vehicleId with fallback chain
    const vehicleId = getVehicleId();
    console.log('[ItineraryDispatch] Using vehicleId:', vehicleId);
    
    // Process itinerary steps (smart filtering - keeps completed steps appearing after incomplete ones)
    const processed = processItinerarySteps(routeDetails.itinerary, routeDetails.trips);
    
    // Add vehicleId and routeId to each step
    const enriched = processed.map(step => ({
      ...step,
      vehicleId: vehicleId,
      routeId: routeDetails.routeId
    }));

    //filter out steps assocuiated with cancelled trips
    const filtered = enriched.filter(step => {
      if (step.tripId) {
        const trip = routeDetails.trips.find(t => t.Id === step.tripId || t.id === step.tripId);  
        if (trip && trip.Status && trip.Status.toLowerCase() === 'cancelled') {
          return false; // Exclude cancelled trip steps
        }
      }
      return true;
    });
    
    setSchedule(filtered);
    
    // Find current waypoint (first INCOMPLETE step, skips completed ones)
    const current = findCurrentWaypoint(enriched);
    console.log('[ItineraryDispatch] Current waypoint:', current ? {
      type: current.type,
      tripId: current.TripId,
      isCompleted: current.isCompleted
    } : 'null (all completed)');
    setCurrentWaypoint(current);
    
    // Check for active break in itinerary data
    // Note: Check multiple fields since raw itinerary uses StepDescription/StepType/breakStep
    const breakStep = routeDetails.itinerary.find(step => {
      const stepType = (step.type || step.StepType || step.StepDescription || '').toLowerCase();
      const isBreakStep = stepType === 'break' || step.breakStep === true;
      const isArrived = step.StepStatus === 'arrived' || step.isArrived === true;
      return isBreakStep && isArrived && !step.isCompleted;
    });
    // Enrich breakStep with vehicleId and routeId if found
    if (breakStep) {
      setActiveBreak({
        ...breakStep,
        vehicleId: vehicleId,
        routeId: routeDetails.routeId
      });
    } else {
      setActiveBreak(null);
    }
    
    // If no incomplete steps remain, navigate to CompleteShift
    if (!current && enriched.length === 0) {
      console.log('All itinerary steps completed - navigating to CompleteShift');
      if (typeof navigateTo === 'function') {
        navigateTo('completeShift');
      }
    }
  }, [routeDetails, getVehicleId, navigateTo]);
  
  // Send GPS update to server (throttled to every 30 seconds)
  const sendGPSUpdateToServer = useCallback((location) => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastServerUpdateRef.current;
    
    // Only send to server if 30 seconds have passed since last update
    if (timeSinceLastUpdate >= 30000) {
      const vehicleId = getVehicleId();
      if (vehicleId && routeDetails && routeDetails.routeId) {
        console.log('ItineraryDispatch - Sending GPS_UPDATE to server:', {
          lat: location.lat,
          lng: location.lng,
          vehicleId: vehicleId,
          routeId: routeDetails.routeId
        });
        
        comms.sendGPSUpdate(
          location.lat,
          location.lng,
          location.speed ?? 0, // speed (mph)
          vehicleId,
          routeDetails.routeId,
          'route',
          null,
          selectedVehicle
        );
        
        lastServerUpdateRef.current = now;
      }
    }
  }, [getVehicleId, routeDetails, selectedVehicle]);
  
  const handleLocationUpdate = (newLocation) => {
    // Store the latest location
    lastLocationRef.current = newLocation;
    
    // Immediately update components for real-time functionality
    setCenter(newLocation);
    setVehiclePosition(newLocation);
    if (map) {
      map.panTo(newLocation);
    }
    
    // Send GPS update to server (throttled)
    sendGPSUpdateToServer(newLocation);
  };
  
  const onLoad = useCallback((loadedMap) => {
    setMap(loadedMap);
  }, []);
  
  // Handle starting a break from BreakModal
  const handleStartBreak = useCallback((breakStep) => {
    if (!breakStep || !vehiclePosition) {
      console.error('Cannot start break - missing step or location');
      return;
    }
    
    sendStartBreak(
      {
        StepId: breakStep.StepId || breakStep.stepId,
        vehicleId: breakStep.vehicleId || getVehicleId(),
        routeId: breakStep.routeId || routeDetails?.routeId
      },
      vehiclePosition
    );
  }, [vehiclePosition, getVehicleId, routeDetails]);
  
  // Handle requesting a split shift from BreakModal
  const handleRequestSplitShift = useCallback((breakStep) => {
    if (!breakStep || !vehiclePosition) {
      console.error('Cannot request split shift - missing step or location');
      return;
    }
    
    sendRequestSplitShift(
      {
        StepId: breakStep.StepId || breakStep.stepId,
        vehicleId: breakStep.vehicleId || getVehicleId(),
        routeId: breakStep.routeId || routeDetails?.routeId
      },
      vehiclePosition
    );
  }, [vehiclePosition, getVehicleId, routeDetails]);
  
  // Handle dismissing itinerary change toast
  const handleDismissChangeToast = useCallback(() => {
    setItineraryChangeToast(null);
  }, []);
  
  if (!routeDetails) {
    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-slate-100 px-4">
        <div className="max-w-md rounded-lg bg-white p-6 text-center shadow">
          <h2 className="text-lg font-semibold text-slate-900">No Route Assigned</h2>
          <p className="mt-2 text-sm text-slate-600">
            We couldn&apos;t find any itinerary data for the current driver.
          </p>
          {typeof onBack === 'function' && (
            <button
              onClick={onBack}
              className="mt-4 inline-flex items-center rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              Back
            </button>
          )}
        </div>
      </div>
    );
  }
  
  if (loading) {
    return <div>Loading...</div>;
  }

  // Layout styles for portrait/landscape
  const mapSectionStyle = isPhone
    ? { minHeight: '33vh', padding: '5px', display: 'flex', justifyContent: 'center', alignItems: 'center' }
    : { padding: '5px', flex: '1', display: 'flex', justifyContent: 'center', alignItems: 'center' };

  const currentWaypointStyle = isPhone
    ? { flexShrink: 0 }
    : { flexShrink: 0 };

  const itinerarySectionStyle = isPhone
    ? { flex: '1', overflowY: 'auto' }
    : { flex: '1', overflowY: 'auto' };

  return (
    <div className="bg-slate-100" style={{ height: `calc(100vh - 65px)` }}>
      {/* Connection error toast */}
      {connectionError && (
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm">
          {connectionError}
        </div>
      )}
      
      {/* Itinerary change toast - persistent until dismissed */}
      {itineraryChangeToast && (
        <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 bg-amber-500 text-white px-4 py-3 rounded-lg shadow-lg max-w-sm">
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <p className="font-semibold">Schedule Changed</p>
              <p className="text-sm">
                {itineraryChangeToast.added > 0 && `${itineraryChangeToast.added} trip${itineraryChangeToast.added > 1 ? 's' : ''} added`}
                {itineraryChangeToast.added > 0 && itineraryChangeToast.removed > 0 && ', '}
                {itineraryChangeToast.removed > 0 && `${itineraryChangeToast.removed} trip${itineraryChangeToast.removed > 1 ? 's' : ''} removed`}
                {itineraryChangeToast.updated && itineraryChangeToast.added === 0 && itineraryChangeToast.removed === 0 && 'Your schedule has been updated'}
              </p>
            </div>
            <button
              onClick={handleDismissChangeToast}
              className="bg-white text-amber-600 font-bold px-3 py-1 rounded hover:bg-amber-100 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
      
      <GeolocationHandler
        onLocationUpdate={handleLocationUpdate}
        setIsAccurate={setIsAccurate}
        vehicleId={getVehicleId()}
        fleetSettings={fleetSettings}
      />

      {googleApiKey && center && (
        <div className={isPhone ? 'flex flex-col h-full' : 'flex flex-row h-full'}>
          {!isPhone ? (
            /* Desktop/Landscape layout: map on left, currentwaypoint + itinerary on right */
            <>
              {/* Map section - full left side */}
              <div ref={mapContainerRef} style={mapSectionStyle}>
                <ItineraryMap
                  googleApiKey={googleApiKey}
                  isPhone={isPhone}
                  center={center}
                  vehiclePosition={vehiclePosition}
                  currentWaypoint={currentWaypoint}
                  schedule={schedule}
                  isAccurate={isAccurate}
                  onMapLoad={onLoad}
                  fleetSettings={fleetSettings}
                />
              </div>

              {/* Right side - CurrentWaypoint + Itinerary */}
              <div className="flex flex-col" style={{ flex: '1', overflowY: 'auto' }}>
                {currentWaypoint && schedule.length > 0 && (
                  <div style={currentWaypointStyle}>
                    <ItineraryCurrentWaypoint
                      waypoint={currentWaypoint}
                      expectedTime={null}
                      isAccurate={isAccurate}
                      isPhone={isPhone}
                      currentLocation={vehiclePosition}
                      onShiftComplete={() => navigateTo('completeShift')}
                      activeBreak={activeBreak}
                    />
                  </div>
                )}
                {schedule.length > 1 && (
                  <div style={itinerarySectionStyle}>
                    <ItineraryItinerary
                      isPhone={isPhone}
                      schedule={schedule.slice(1)}
                      currentWaypoint={currentWaypoint}
                      vehicleLocation={vehiclePosition}
                      fleetSettings={fleetSettings}
                      googleMapsLoaded={!!googleApiKey}
                      routeDetails={routeDetails}
                      onStartBreak={handleStartBreak}
                      onRequestSplitShift={handleRequestSplitShift}
                      canFlag={canFlag}
                      onFlagClick={openFlagTrip}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Mobile/Portrait layout: map at top (33vh), currentwaypoint (350px), then itinerary (rest) */
            <>
              {/* Map section - 33vh minimum */}
              <div ref={mapContainerRef} style={mapSectionStyle}>
                <ItineraryMap
                  googleApiKey={googleApiKey}
                  isPhone={isPhone}
                  center={center}
                  vehiclePosition={vehiclePosition}
                  currentWaypoint={currentWaypoint}
                  schedule={schedule}
                  isAccurate={isAccurate}
                  onMapLoad={onLoad}
                  fleetSettings={fleetSettings}
                />
              </div>

              {/* CurrentWaypoint section - fixed 350px */}
              {currentWaypoint && schedule.length > 0 && (
                <div style={currentWaypointStyle}>
                  <ItineraryCurrentWaypoint
                    waypoint={currentWaypoint}
                    expectedTime={null}
                    isAccurate={isAccurate}
                    isPhone={isPhone}
                    currentLocation={vehiclePosition}
                    onShiftComplete={() => navigateTo('completeShift')}
                    activeBreak={activeBreak}
                  />
                </div>
              )}

              {/* Itinerary section - rest of viewport */}
              {schedule.length > 1 && (
                <div style={itinerarySectionStyle}>
                  <ItineraryItinerary
                    isPhone={isPhone}
                    schedule={schedule.slice(1)}
                    currentWaypoint={currentWaypoint}
                    vehicleLocation={vehiclePosition}
                    fleetSettings={fleetSettings}
                    googleMapsLoaded={!!googleApiKey}
                    routeDetails={routeDetails}
                    onStartBreak={handleStartBreak}
                    onRequestSplitShift={handleRequestSplitShift}
                    canFlag={canFlag}
                    onFlagClick={openFlagTrip}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!isAccurate && (
        <div className="fixed top-16 left-0 mt-1 p-1 z-1 rounded-tr rounded-br bg-red-500 text-white opacity-50">
          <span className="text-xs">!GPS</span>
        </div>
      )}

      {/* Phase B — itinerary-mode flag trip */}
      <FlagTripCreateModal
        isOpen={showFlagTripModal}
        onClose={() => setShowFlagTripModal(false)}
        routeId={routeDetails?.routeId}
        vehicleId={getVehicleId()}
        driverId={driverData?.dispatchData?.driverId}
        currentLocation={vehiclePosition}
        fleetSettings={fleetSettings}
        googleMapsLoaded={!!googleApiKey}
        onSuccess={() => setShowFlagTripModal(false)}
      />
    </div>
  );
}

export default ItineraryDispatch;
